/**
 * BẢN ĐỒ TỦ — GD-17 B3. Một thành phần, hai chỗ dùng: ô giữa trang Tổng quan và trang `/ban-do`
 * (nút "Xem toàn màn hình"). Không có bản thứ hai.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ⛔ CHẤM TỦ VẼ KHI `lat/lng` HỢP LỆ (số, khác 0, trong -90..90 / -180..180) — GD-17 vòng 2,
 *   dev_device: KHÔNG phụ thuộc `gpsFix`. `gpsFix` chỉ nói NGUỒN của toạ độ, và nói ra trong popup:
 *   "Toạ độ tủ tự đo" (gpsFix) · "Toạ độ nhập tay" (còn lại, kể cả khi máy chủ chưa gửi gpsFix).
 *   "N tủ chưa có toạ độ" = đúng số tủ KHÔNG vẽ; "Chưa có tủ nào gửi toạ độ" chỉ khi N = tổng
 *   (và khi ấy dòng N không lặp lại cùng một điều).
 * ⛔ MÀU THEO LUẬT NGÂN SÁCH (tệp 29 điều cấm 9, chờ duyệt): xanh lá = đang nối · xám = mất kết
 *   nối · vàng = có việc cần xem · xám nhạt = chưa gán tuyến. **0 đỏ.** Chấm 10 px, viền 1 px màu
 *   nền. Màu đọc từ token lúc vẽ (Leaflet ghi `fill` bằng thuộc tính SVG — `var()` không chạy ở đó).
 * ⛔ NỀN BẢN ĐỒ HỎNG KHÔNG ĐƯỢC LÀM TRẮNG TRANG, KHÔNG ĐƯỢC SPAM CONSOLE: thử MỘT ô nền (z=0)
 *   trước khi gắn lớp nền; hỏng ⇒ không gắn lớp nào, vẽ lưới xám + "Không tải được nền bản đồ",
 *   chấm tủ vẫn vẽ. Kết quả thử giữ cho cả lượt mở trang (không thử lại theo vòng lặp). Lớp nền
 *   đã gắn mà ô nào đó lỗi (`tileerror`) ⇒ gỡ lớp, về cùng nhánh ấy.
 * `VITE_BAN_DO_NEN`: mẫu URL ô nền (`{z}/{x}/{y}`, `{s}` nếu có). Vắng ⇒ OSM QUA MÁY CHỦ (`/o-ban-do`, TK-26). `tat` hoặc rỗng ⇒
 *   không nền (máy chủ công ty không ra được internet).
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
import 'leaflet/dist/leaflet.css';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import { AttributionControl, MapContainer, Marker, Popup, ScaleControl, TileLayer, useMap, useMapEvents, ZoomControl } from 'react-leaflet';
/*
 * TK-34 lượt 1 — nền vector (đường A), tải LƯỜI: MapLibre ~830 kB (gzip ~230 kB) chỉ tải khi một bản đồ thật sự dựng; trang
 *   không có bản đồ không trả giá ấy, và trang có bản đồ vẽ tủ/đèn trước, nền tới sau (mất nền ≠ mất dữ liệu).
 */
const NenVector = lazy(() => import('./NenVector').then((m) => ({ default: m.NenVector })));
import type { Alarm, TreeRoute } from '../lib/types';
import { TOA_DO_MAC_DINH } from '../lib/mattroi';
import { mucCua } from '../lib/vande';

/*
 * TK-26 (10/10/2026): ô nền đi QUA MÁY CHỦ CỦA MÌNH (`/o-ban-do/…` → nginx → tile.openstreetmap.org, đệm 7 ngày).
 *   lee: máy bảng (app cài ra màn hình chính) "Không tải được nền bản đồ" dù TK-24 đã sửa phép thử và sw.js ⇒ máy bảng
 *   không gọi thẳng được máy chủ ô OSM. Cùng máy chủ thì cùng đường mạng và chứng chỉ trang đang chạy được.
 *   Dòng ghi công "© OpenStreetMap" GIỮ (điều khoản OSM) — điều kiện bên dưới so với hằng này.
 */
export const NEN_MAC_DINH = '/o-ban-do/{z}/{x}/{y}.png';

/** Mẫu URL nền đang dùng; `null` = tắt nền. */
export function mauNen(): string | null {
  const v = import.meta.env.VITE_BAN_DO_NEN as string | undefined;
  if (v === undefined) return NEN_MAC_DINH;
  const t = v.trim();
  return !t || t === 'tat' ? null : t;
}

type TrangThaiNen = 'dang-thu' | 'ok' | 'hong' | 'tat';

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * 🔴 09/10/2026 — MỘT Ô HỎNG KHÔNG ĐƯỢC GIẾT CẢ NỀN BẢN ĐỒ.
 *
 * Lee dùng thật trên máy bảng: bản đồ chỉ còn các ô vuông. Trên máy bàn cùng trang thì chạy.
 * Dò ra ba chỗ cộng lại thành triệu chứng ấy, và cả ba đều ở phía mình:
 *
 *   1. Phép thử chỉ thử **một** ô (`0/0/0`) với hạn 8 giây. Một nhịp mạng xấu đúng lúc ấy là
 *      cả bản đồ tắt — dù mọi ô khác tải được.
 *   2. Kết quả giữ trong một `Promise` ở tầm mô-đun ⇒ **hỏng một lần là hỏng cả phiên**; chỉ
 *      đóng hẳn app mở lại mới thử lại. Trên app cài ra màn hình chính, "mở lại" không phải
 *      thao tác ai cũng nghĩ ra.
 *   3. `tileerror` của MỘT ô gọi thẳng `baoHong()` ⇒ gỡ luôn cả lớp nền.
 *
 * Chữa: thử tối đa 3 lần (0.6s → 1.8s), đếm lỗi thay vì tắt ngay, và cho một nút **Thử lại**.
 * ⛔ KHÔNG bỏ hẳn phép thử: nó là thứ phân biệt "nền chưa tải" với "nền trống" — bỏ đi thì
 *   lưới xám hiện ra mà không ai nói vì sao, đúng cái lee phải tự đoán lần này.
 */
const SO_LAN_THU = 3;
const SO_LOI_O_CHO_PHEP = 8;   // Leaflet tải ~20 ô một khung; vài ô hỏng là bình thường.

function thuMotO(mau: string, msCho: number): Promise<boolean> {
  return new Promise((xong) => {
    const img = new Image();
    const hen = window.setTimeout(() => { img.src = ''; xong(false); }, msCho);
    img.onload = () => { window.clearTimeout(hen); xong(true); };
    img.onerror = () => { window.clearTimeout(hen); xong(false); };
    // Ô thật ở vùng đang dùng, kèm tham số phá bộ nhớ đệm để lần "Thử lại" là một phép đo mới.
    img.src = mau
      .replace('{s}', 'a').replace('{z}', '6').replace('{x}', '51').replace('{y}', '31').replace('{r}', '')
      + (mau.includes('?') ? '&' : '?') + 'thu=' + Date.now();
  });
}

async function thuTaiNen(mau: string): Promise<boolean> {
  for (let i = 0; i < SO_LAN_THU; i++) {
    if (await thuMotO(mau, 6000)) return true;
    if (i < SO_LAN_THU - 1) await new Promise((r) => setTimeout(r, 600 * (i + 1) * 2));
  }
  return false;
}

function useNen(): { mau: string | null; tt: TrangThaiNen; baoHong: () => void; thuLai: () => void } {
  const mau = mauNen();
  const [tt, setTt] = useState<TrangThaiNen>(mau ? 'dang-thu' : 'tat');
  const [lan, setLan] = useState(0);
  const soLoi = useRef(0);
  useEffect(() => {
    if (!mau) return;
    let con = true;
    soLoi.current = 0;
    setTt('dang-thu');
    void thuTaiNen(mau).then((ok) => { if (con) setTt(ok ? 'ok' : 'hong'); });
    return () => { con = false; };
  }, [mau, lan]);
  return {
    mau,
    tt,
    /*
     * ⚠ ĐẾM, KHÔNG TẮT NGAY. `tileerror` bắn cho từng ô; vài ô rơi khi vừa kéo bản đồ là
     *   chuyện thường. Chỉ khi quá ngưỡng mới coi là nền hỏng.
     */
    baoHong: () => {
      soLoi.current += 1;
      if (soLoi.current > SO_LOI_O_CHO_PHEP) setTt('hong');
    },
    thuLai: () => { soLoi.current = 0; setLan((v) => v + 1); },
  };
}

/* TK-33 (mẫu bản 3): thêm loại `khan` — tủ có hồ sơ KHẨN (mức 1) là chấm ĐỎ, tách khỏi "Cảnh báo" vàng. */
export type LoaiCham = 'noi' | 'mat' | 'viec' | 'khan' | 'chua-gan';

/** Lớp token cho chú giải (chấm SVG) — cùng các loại với chấm trên bản đồ. "Chưa gán tuyến" chỉ hiện khi có. */
export const CHU_GIAI: { loai: LoaiCham; ten: string; lop: string }[] = [
  /* TK-33b (mẫu chạy được): "Hoạt động" thay "Bình thường". */
  { loai: 'noi', ten: 'Hoạt động', lop: 'text-tot' },
  { loai: 'viec', ten: 'Cảnh báo', lop: 'text-canh' },
  { loai: 'khan', ten: 'Khẩn', lop: 'text-nguy' },
  { loai: 'mat', ten: 'Mất kết nối', lop: 'text-chu-3' },
  { loai: 'chua-gan', ten: 'Chưa gán tuyến', lop: 'text-vien-manh' },
];
const TOKEN_CUA: Record<LoaiCham, string> = { noi: '--tot', mat: '--chu-3', viec: '--canh', khan: '--nguy', 'chua-gan': '--vien-manh' };

/** `rgb(r, g, b)` của một token `thietke.css` ("46 224 111") — đọc lúc vẽ để theo bộ sáng/tối. */
function mauToken(ten: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(ten).trim().split(/\s+/).join(', ');
  return v ? `rgb(${v})` : 'rgb(95, 117, 151)';
}

export interface ChamTu {
  id: string; ten: string; ma: string; tuyen: string | null;
  lat: number; lng: number; loai: LoaiCham; noi: boolean;
  /** `true` = toạ độ tủ tự đo (gpsFix); `false` = nhập tay / chưa biết nguồn. */
  tuDo: boolean;
  /** TK-33 popup: lần cuối tủ báo (dòng "Mất kết nối từ HH:MM") · số ngõ ra bật (tủ tự khai; chỉ khi đang nối). */
  lanCuoi: string | null; ngoRaBat: number | null;
}

/** "HH:MM" giờ máy của một mốc ISO. */
function gioPhut(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
/** Dòng trạng thái MỘT dòng của popup tủ. */
export function dongTrangThai(c: Pick<ChamTu, 'loai' | 'noi' | 'lanCuoi'>): string {
  if (c.loai === 'khan') return 'Khẩn';
  if (c.loai === 'viec') return 'Cảnh báo';
  if (c.noi) return 'Trực tuyến';   // TK-46 §4 (N8 TK-44): "Đang kết nối" đọc như đang thử nối ⇒ "Trực tuyến"
  return c.lanCuoi ? `Mất kết nối từ ${gioPhut(c.lanCuoi)}` : 'Chưa kết nối lần nào';
}

/** Toạ độ vẽ được: số hữu hạn, khác 0, trong dải địa lý. Một chỗ quyết cho bản đồ. */
export function toaDoHopLe(lat: unknown, lng: unknown): boolean {
  return typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng)
    && lat !== 0 && lng !== 0 && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

/** Tách tủ có/không định vị từ cây tuyến + cảnh báo đang mở. */
export function chamTuTuCay(cay: TreeRoute[] | undefined, canhBao: Alarm[] | undefined): { cham: ChamTu[]; khongToaDo: number; tong: number } {
  /* TK-25: hồ sơ "Mất kết nối" KHÔNG tô vàng chấm — tủ im là chấm XÁM (`mat`, đọc từ `status`), như cũ. Tô vàng nó
     là nói "có việc cần xem" về một tủ mà máy chủ không thấy gì để xem. */
  const coViec = new Set((canhBao ?? []).filter((a) => a.code !== 'MAT_KET_NOI').map((a) => a.cabinetId));
  /* TK-33: trong các tủ có việc, tủ có hồ sơ KHẨN (cùng `mucCua` của khung Vấn đề) là chấm đỏ. */
  const coKhan = new Set((canhBao ?? []).filter((a) => mucCua(a) === 'khan').map((a) => a.cabinetId));
  const cham: ChamTu[] = [];
  let khongToaDo = 0, tong = 0;
  for (const r of cay ?? []) {
    for (const c of r.cabinets ?? []) {
      tong++;
      if (!toaDoHopLe(c.lat, c.lng)) { khongToaDo++; continue; }
      const noi = c.status === 'ONLINE';
      const loai: LoaiCham = coKhan.has(c.id) ? 'khan' : coViec.has(c.id) ? 'viec' : noi ? 'noi' : 'mat';
      cham.push({ id: c.id, ten: c.name, ma: c.code, tuyen: r.tenDayDu ?? null, lat: c.lat as number, lng: c.lng as number, loai, noi, tuDo: c.gpsFix === true,
        lanCuoi: c.lastSeenAt ?? null, ngoRaBat: noi && Array.isArray(c.outputs) ? c.outputs.filter((o) => o.isOn).length : null });
    }
  }
  return { cham, khongToaDo, tong };
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * 🔴 TK-24 (09/10/2026) — GOM CỤM. Lee (report): *"Tôi giả sử các tủ tôi trải dài vài tỉnh, vài
 *   miền thì lúc đó bạn hiển thị làm sao cho tối ưu"*.
 *
 * Trước: mỗi tủ một chấm 5 px, không gom. 100 tủ trong một thành phố (đo trên dữ liệu giả bộ B,
 * ảnh `anh/truoc/tongquan_1440.png`) thành một đám chấm chồng nhau, không đếm, không bấm trúng.
 * Trải nhiều tỉnh thì `fitBounds` ôm cả nước và mỗi tỉnh là một vệt.
 *
 * Cách gom: chia MÀN HÌNH thành ô 56 px ở mức phóng hiện tại (`map.project`), tủ cùng ô thành một
 * cụm. Phóng vào thì ô 56 px bé lại theo mét ⇒ cụm tự tách. Không thêm thư viện (`package.json`
 * chỉ lee/dev_device sửa — AGENTS.md).
 * ⚠ Màu cụm = trạng thái XẤU NHẤT trong cụm (có việc > mất kết nối > chưa gán > đang nối): một cụm
 *   50 tủ xanh có 1 tủ cần xem thì cụm phải vàng, nếu không thì gom cụm giấu mất đúng cái tủ ấy.
 * ⚠ Chỉ tính lại khi ĐỔI MỨC PHÓNG, không tính khi kéo: ô chia theo toạ độ chiếu, không theo khung nhìn.
 * ⚠ Bấm cụm ⇒ phóng vào đúng khung của cụm (maxZoom 17); cụm nằm trong 1 ô 56 px ở zoom 17 là tủ
 *   thật sự chồng toạ độ — lúc ấy vẫn còn cụm và bấm sẽ mở popup liệt kê.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
const O_CUM_PX = 56;
const UU_TIEN_LOAI: LoaiCham[] = ['khan', 'viec', 'mat', 'chua-gan', 'noi'];
interface CumTu { khoa: string; lat: number; lng: number; items: ChamTu[]; loai: LoaiCham; khung: L.LatLngBounds }

export function gomCum(chieu: (lat: number, lng: number) => { x: number; y: number }, cham: ChamTu[]): { don: ChamTu[]; cum: CumTu[] } {
  const o = new Map<string, ChamTu[]>();
  for (const c of cham) {
    const p = chieu(c.lat, c.lng);
    const k = `${Math.floor(p.x / O_CUM_PX)}:${Math.floor(p.y / O_CUM_PX)}`;
    const ds = o.get(k); if (ds) ds.push(c); else o.set(k, [c]);
  }
  const don: ChamTu[] = []; const cum: CumTu[] = [];
  for (const [khoa, items] of o) {
    if (items.length === 1) { don.push(items[0]); continue; }
    const lat = items.reduce((a, c) => a + c.lat, 0) / items.length;
    const lng = items.reduce((a, c) => a + c.lng, 0) / items.length;
    const loai = UU_TIEN_LOAI.find((l) => items.some((c) => c.loai === l)) ?? 'noi';
    cum.push({ khoa, lat, lng, items, loai, khung: L.latLngBounds(items.map((c) => [c.lat, c.lng] as [number, number])) });
  }
  return { don, cum };
}

/** Đường kính cụm theo số tủ: 30 px (2 tủ) → 46 px (≥ 100 tủ). Số nằm trong, chữ 12 px đậm. */
function coCum(so: number): number { return Math.min(46, 30 + Math.round(Math.log10(so) * 16)); }

/** `rgb(r, g, b)` → `rgba(r, g, b, a)` (màu đọc từ token lúc vẽ). */
function aMau(m: string, a: number): string { return m.replace('rgb(', 'rgba(').replace(')', `, ${a})`); }

/**
 * TK-34 lượt 2 (ảnh `V3_mau.png`): marker tủ Ô VUÔNG 28 px ĐẶC MÀU trạng thái + biểu tượng tủ (lucide `server`) màu
 * `--tren-nhan`, viền cùng màu nhạt, vòng mờ quanh; tủ ĐANG CHỌN nền `nhan`, viền `--tren-nhan`, vòng rộng hơn. Mất kết nối
 * vẫn XÁM (màu đọc từ `mau`, không đổi luật). Lớp `cham-tu cham-<loại> cham-nguon-…` giữ như cũ.
 */
const SVG_TU = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="20" height="8" x="2" y="2" rx="2"/><rect width="20" height="8" x="2" y="14" rx="2"/><line x1="6" x2="6.01" y1="6" y2="6"/><line x1="6" x2="6.01" y1="18" y2="18"/></svg>';
function iconTu(c: ChamTu, mau: Record<LoaiCham, string>, chon: boolean, nhan: string): L.DivIcon {
  const m = mau[c.loai];
  const nen = chon ? nhan : m;
  const vien = chon ? 'rgb(var(--tren-nhan))' : aMau(m, 0.5);
  const vong = chon ? `0 0 0 6px ${aMau(nhan, 0.25)}` : `0 0 0 4px ${aMau(m, 0.18)}`;
  return L.divIcon({
    className: `cham-tu cham-${c.loai} cham-nguon-${c.tuDo ? 'tu-do' : 'nhap-tay'}${chon ? ' dang-chon' : ''}`,
    iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -14],
    html: `<div data-tu-ban-do="${c.id}" style="width:28px;height:28px;box-sizing:border-box;border-radius:6px;border:2px solid ${vien};background:${nen};box-shadow:${vong};display:grid;place-items:center;color:rgb(var(--tren-nhan));line-height:0">${SVG_TU}</div>`,
  });
}

/* TK-34 lượt 2: từ mức phóng này trở lên KHÔNG gom cụm — mẫu V3 thấy từng tủ và đường tuyến của nó (= `ZOOM_HIEN` lớp đèn). */
export const ZOOM_KHONG_GOM = 14;
/* TK-34 lượt 3 (ảnh cạnh mẫu): 16 ⇒ 15 — mẫu V3 chọn tủ mà vẫn thấy các tủ cùng khu (thước 500 m); 16 chỉ còn một tủ. ≥ 14 nên đèn + đường vẫn hiện. */
const ZOOM_BAY_TOI = 15;

function LopCham({ cham, mau, nhan, nhanSang, vien, popupTu, onChonTu, tuChon, lanChon, hienSuCo, moPopupKhiChon = true }: {
  cham: ChamTu[]; mau: Record<LoaiCham, string>; nhan: string; nhanSang: string; vien: string;
  popupTu?: (cabinetId: string) => ReactNode; onChonTu?: (cabinetId: string) => void;
  tuChon?: string | null; lanChon?: number; hienSuCo?: boolean; moPopupKhiChon?: boolean;
}) {
  const map = useMap();
  /* Vận hành 24/10 (đo: bấm tuyến ⇒ setView z15 nhưng tủ số 1 vẫn nằm trong cụm "3"): zoom bản chép trong state kẹt ở mức cũ khi bản
     đồ tự nhảy (setView không hoạt hình) — cùng lỗi TK-38 đã gặp ở lớp đèn. Đọc THẲNG từ bản đồ mỗi lần vẽ; sự kiện chỉ để vẽ lại. */
  const [, setNhip] = useState(0);
  useMapEvents({ zoomend: () => setNhip((n) => n + 1), moveend: () => setNhip((n) => n + 1) });
  const zoom = map.getZoom();
  /* Tủ đang chọn KHÔNG gom vào cụm — nó phải luôn nhìn thấy được và mở popup được. */
  const { don, cum } = useMemo(
    () => {
      const g = zoom >= ZOOM_KHONG_GOM ? { don: cham.filter((c) => c.id !== tuChon), cum: [] }
        : gomCum((lat, lng) => map.project([lat, lng], zoom), cham.filter((c) => c.id !== tuChon));
      const chon = cham.find((c) => c.id === tuChon);
      return chon ? { don: [...g.don, chon], cum: g.cum } : g;
    },
    [cham, map, zoom, tuChon],
  );
  /*
   * TK-33b — CHỌN TỦ (cây · bản đồ · nút Định vị cùng ghi `?tu=`): bản đồ BAY tới tủ (zoom ≥ `ZOOM_BAY_TOI`) rồi MỞ popup của nó, như
   *   `selectDevice` của mẫu. `lanChon` đổi mỗi lần chọn ⇒ chọn lại cùng tủ vẫn bay lại.
   */
  const refs = useRef(new Map<string, L.Marker>());
  const chamRef = useRef(cham); chamRef.current = cham;
  /* Mở thẳng `/?tu=`: cây tuyến về SAU lần vẽ đầu ⇒ phải bay lại khi tủ ấy XUẤT HIỆN, không chỉ khi `tuChon` đổi. */
  const coTuChon = !!tuChon && cham.some((c) => c.id === tuChon);
  useEffect(() => {
    if (!tuChon) return;
    const c = chamRef.current.find((x) => x.id === tuChon);
    if (!c) return;
    let huy = false;
    const mo = () => { window.setTimeout(() => { if (!huy) refs.current.get(tuChon)?.openPopup(); }, 80); };
    /* TK-47 §2: Vận hành không tự mở popup (khối nổi đè bản đồ) — tủ đang chọn đã có ở chip + bảng tủ; bấm chấm vẫn mở */
    if (moPopupKhiChon) map.once('moveend', mo);
    map.flyTo([c.lat, c.lng], Math.max(map.getZoom(), ZOOM_BAY_TOI), { duration: 0.6 });
    return () => { huy = true; map.off('moveend', mo); };
  }, [tuChon, lanChon, coTuChon, map, moPopupKhiChon]);
  return (
    <>
      {hienSuCo && cham.filter((c) => c.loai === 'khan').map((c) => (
        /* Mẫu: dấu SỰ CỐ cạnh tủ có hồ sơ khẩn — tam giác đỏ, bấm ⇒ chọn tủ. */
        <Marker key={`sc-${c.id}`} position={[c.lat, c.lng]} zIndexOffset={-100}
          icon={L.divIcon({ className: 'tam-giac-su-co', iconSize: [18, 16], iconAnchor: [-6, 22],
            html: `<div data-o="tam-giac-su-co" style="width:0;height:0;border-left:9px solid transparent;border-right:9px solid transparent;border-bottom:16px solid ${mau.khan}"></div>` })}
          eventHandlers={onChonTu ? { click: () => onChonTu(c.id) } : undefined} />
      ))}
      {don.map((c) => (
        <Marker
          key={c.id}
          position={[c.lat, c.lng]}
          icon={iconTu(c, mau, c.id === tuChon, nhan)}
          zIndexOffset={c.id === tuChon ? 1000 : 0}
          ref={(m) => { if (m) refs.current.set(c.id, m); else refs.current.delete(c.id); }}
          eventHandlers={onChonTu ? { click: () => onChonTu(c.id) } : undefined}
        >
          {/* Đệm tự cuộn phía trên = chỗ thanh công cụ + hộp lớp đèn, để popup không chui dưới chúng. */}
          {/* TK-35 lượt 3 (TK-34 treo): popup mở LỆCH SANG PHẢI marker như mẫu V3 (không che đèn phía trên tủ); không mũi nhọn. */}
          {/* TK-48 (kiểm vòng 1): Vận hành (`moPopupKhiChon=false`) không gắn popup — khung điều khiển thay; popup tự dời bản đồ (autoPan)
              đẩy marker đang chọn ra ngoài bản đồ ở 390 px. /ban-do giữ popup. */}
          {moPopupKhiChon && (
            <Popup offset={[200, 150]} className="popup-tu-phai" autoPanPaddingTopLeft={[20, 140]} autoPanPaddingBottomRight={[60, 40]}>
              <div className="space-y-0.5 text-sm" data-popup-tu={c.id}>
                <p className="m-0 font-semibold">{c.ten}{c.ma && <span className="font-normal text-chu-3"> ({c.ma})</span>}</p>
                <p className={`m-0 font-medium ${c.loai === 'khan' ? 'text-nguy' : c.loai === 'viec' ? 'text-canh' : c.noi ? 'text-tot' : 'text-chu-3'}`} data-o="popup-trang-thai">{dongTrangThai(c)}</p>
                {/* TK-34 lượt 3: popup gọn như mẫu — 3 dòng + 1 nút; nguồn toạ độ chuyển vào title của dòng tuyến. */}
                <p className="m-0 text-chu-2" title={c.tuDo ? 'Toạ độ tủ tự đo' : 'Toạ độ nhập tay'} data-nguon-toa-do>{c.ngoRaBat !== null && <span title="Tủ tự khai">{c.ngoRaBat} ngõ ra bật · </span>}{c.tuyen ?? 'Chưa gán tuyến'}</p>
                {popupTu?.(c.id)}
                <p className="m-0 pt-1">
                  {onChonTu
                    ? <button type="button" onClick={() => onChonTu(c.id)} className="border-0 bg-transparent p-0 font-semibold text-nhan hover:underline" data-o="popup-mo-bang">Mở bảng tủ →</button>
                    : <Link to={`/tu/${c.id}`} className="font-semibold text-nhan hover:underline">Mở tủ</Link>}
                </p>
              </div>
            </Popup>
          )}
        </Marker>
      ))}
      {cum.map((k) => {
        const d = coCum(k.items.length);
        const soViec = k.items.filter((c) => c.loai === 'viec' || c.loai === 'khan').length;
        const soMat = k.items.filter((c) => !c.noi).length;
        const icon = L.divIcon({
          className: `cum-tu cum-${k.loai}`,
          iconSize: [d, d], iconAnchor: [d / 2, d / 2],
          html: `<span style="display:grid;place-items:center;width:${d}px;height:${d}px;border-radius:999px;background:${mau[k.loai]};border:2px solid ${vien};color:#fff;font-weight:600;font-size:12px;line-height:1;box-shadow:0 0 0 4px ${aMau(mau[k.loai], 0.2)}">${k.items.length}</span>`,
        });
        return (
          <Marker key={k.khoa} position={[k.lat, k.lng]} icon={icon}
            eventHandlers={{ click: () => { if (map.getZoom() < 17) map.fitBounds(k.khung, { padding: [32, 32], maxZoom: 17 }); } }}>
            <Popup>
              <div className="space-y-0.5 text-sm" data-popup-cum={k.khoa}>
                <p className="m-0 font-semibold">{k.items.length} tủ</p>
                <p className="m-0 text-chu-2">
                  {soViec > 0 ? `${soViec} có việc cần xem` : 'Không có việc cần xem'}
                  {soMat > 0 ? ` · ${soMat} mất kết nối` : ''}
                </p>
                {map.getZoom() >= 17 && (
                  <ul className="m-0 max-h-40 list-none overflow-y-auto p-0">
                    {k.items.map((c) => (
                      <li key={c.id}><Link to={`/tu/${c.id}`} className="text-nhan hover:underline">{c.ten}</Link>{c.ma && <span className="text-chu-3"> ({c.ma})</span>}</li>
                    ))}
                  </ul>
                )}
                {map.getZoom() < 17 && <p className="m-0 text-chu-3">Bấm để phóng vào</p>}
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
}

/** Khớp khung theo các chấm; đổi kích thước ô thì báo Leaflet đo lại. */
function KhopKhung({ diem, lan = 0, nhuong = false }: { diem: [number, number][]; lan?: number; nhuong?: boolean }) {
  const map = useMap();
  /*
   * TK-34 lượt 3 (đo 22/10, 1280: 1/3 lần thước 5 km thay 300 m): khoá cũ ghép điểm THEO THỨ TỰ ⇒ tủ đổi trạng thái / danh sách
   *   về thêm làm khoá đổi ⇒ ôm lại khung SAU cú bay tới tủ đang chọn. Nay: khoá theo TẬP điểm (sắp xếp), và khi đang có tủ được
   *   chọn có toạ độ (`nhuong`) thì cú bay thắng — chỉ `lan` đổi (người bấm ôm khung) mới ôm lại.
   */
  const khoa = diem.map((d) => d.join(',')).sort().join('|');
  const lanTruoc = useRef(lan);
  useEffect(() => {
    const doiLan = lanTruoc.current !== lan; lanTruoc.current = lan;
    if (nhuong && !doiLan) return;
    /*
     * TK-34 (đo 22/10, 1/3 lần): KHÔNG hoạt hình. Mở thẳng `/?tu=` thì cây về ⇒ khung này ôm tuyến rồi `LopCham` BAY tới tủ
     *   ngay sau; `fitBounds` có hoạt hình thu phóng kết thúc SAU cú bay và đặt khung về toàn tuyến (thước 3 km thay 200 m).
     */
    if (diem.length === 1) map.setView(diem[0], 15, { animate: false });
    else if (diem.length > 1) map.fitBounds(diem, { padding: [24, 24], maxZoom: 16, animate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [khoa, map, lan]);
  useEffect(() => {
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(map.getContainer());
    return () => ro.disconnect();
  }, [map]);
  return null;
}

/** Lưới xám vẽ bằng SVG (không dùng dải màu — tệp 29 mục 1.3). */
const LUOI = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32"><path d="M32 0H0V32" fill="none" stroke="rgba(142,163,196,0.18)" stroke-width="1"/></svg>',
)}")`;

export function BanDoTu({
  cay, canhBao, caoLop = 'h-[340px]', diemKhung = null, lopThem, mapRef, hopTrai, popupTu, onChonTu,
  tuChon = null, lanChon = 0, lanKhop = 0, hienTu = true, hienSuCo = false, nenToi = false, thanhCongCu, chuGiaiThem, ghiChuDuoi, tranKhung = false, moPopupKhiChon = true,
}: {
  cay: TreeRoute[] | undefined; canhBao: Alarm[] | undefined;
  /** TK-33b: tủ đang chọn (marker xanh có vòng · bay tới + mở popup); `lanChon` đổi mỗi lần chọn để chọn lại vẫn bay. */
  tuChon?: string | null; lanChon?: number;
  /** TK-47: false ⇒ chọn tủ chỉ bay tới, KHÔNG tự mở popup (Vận hành: không khối nổi đè bản đồ) */
  moPopupKhiChon?: boolean;
  /** TK-33b: nút định vị — đổi số ⇒ ôm lại khung (tuyến đang chọn / toàn bộ). */
  lanKhop?: number;
  /** TK-33b: ô tích Tủ / Sự cố của thanh công cụ (mẫu). */
  hienTu?: boolean; hienSuCo?: boolean;
  /** TK-33b: "Lớp bản đồ" — nền tối = lớp CSS `filter` lên ô nền như `.map-dark` của mẫu (không đổi nguồn ô). */
  nenToi?: boolean;
  /** TK-33b: thanh công cụ góc trên trái (chọn tuyến · Lớp bản đồ · Tủ/Đèn/Sự cố · định vị); hộp lớp đèn xếp dưới nó. */
  thanhCongCu?: ReactNode;
  /** TK-33b: mục chú giải thêm (vd "Đèn"). */
  chuGiaiThem?: { ten: string; lop: string }[];
  /** TK-33b: dòng nhỏ dưới chú giải (vd khi mô phỏng). */
  ghiChuDuoi?: ReactNode;
  /** TK-33b: khung tràn (trang chính) — không viền, không bo. */
  tranKhung?: boolean;
  /** TK-31d: lớp vẽ THÊM bên trong `MapContainer` (đèn NEMA). 22/10: cả `/ban-do` lẫn Tổng quan, CHỈ khi máy chủ bật NEMA. */
  lopThem?: ReactNode;
  /** TK-31 22/10: hộp cài đặt hiển thị góc TRÊN TRÁI (cùng kiểu hộp chú giải góc phải) — lớp đèn NEMA. Vắng ⇒ không vẽ gì. */
  hopTrai?: ReactNode;
  /** TK-31 22/10: dòng thêm trong popup của MỘT tủ ("N đèn · xem"). */
  popupTu?: (cabinetId: string) => ReactNode;
  /** TK-31 22/10: tủ vừa được bấm (Tổng quan dùng cho link "Sửa vị trí đèn" → `/ban-do?tu=`). */
  onChonTu?: (cabinetId: string) => void;
  /** TK-31d: nhận `L.Map` để thanh sửa vị trí lấy tâm khung nhìn. */
  mapRef?: MutableRefObject<L.Map | null>;
  /** Lớp chiều cao của khung (≥ 320 px). */
  caoLop?: string;
  /** TK-33: nút "Toàn màn hình" nay ở thanh trên thẻ bản đồ của trang gọi — prop giữ để `/ban-do` không đổi lời gọi. */
  toanManHinh?: boolean;
  /**
   * TK-25 — chip tỉnh trên Tổng quan: khung nhìn ôm ĐÚNG các điểm này (mọi chấm vẫn vẽ). `null`/rỗng ⇒ ôm
   *   toàn bộ như cũ. Lee mục 1: "các vùng xa nhau hiển thị sao cho trọn vẹn" — toàn bộ thì mỗi tỉnh là một
   *   cụm; bấm chip là phóng về tỉnh đó.
   */
  diemKhung?: [number, number][] | null;
}) {
  const nen = useNen();
  /* TK-34 thử (đường A): nền VECTOR trước; hỏng ⇒ rơi về nền raster/nền trống như cũ. */
  const [vectorOk, setVectorOk] = useState(true);
  const vectorHong = useCallback(() => setVectorOk(false), []);
  const { cham, khongToaDo, tong } = useMemo(() => chamTuTuCay(cay, canhBao), [cay, canhBao]);
  /* vòng 2: cây chưa về / lỗi API ⇒ KHÔNG nói "Chưa có tủ nào gửi toạ độ" (đó là câu về dữ liệu, không phải về đường mạng) */
  const khongTuNao = Array.isArray(cay) && khongToaDo === tong;   // N = tổng (kể cả 0 tủ) ⇒ câu "Chưa có tủ nào…" thay dòng N
  const diem = useMemo(() => cham.map((c) => [c.lat, c.lng] as [number, number]), [cham]);
  const [mau, setMau] = useState<Record<LoaiCham, string> | null>(null);
  /* TK-47 §1.6: chú giải GẬP được — một nút nhỏ "Chú giải" mở / đóng danh sách chấm màu */
  const [moChuGiai, setMoChuGiai] = useState(true);
  useEffect(() => {
    setMau({ noi: mauToken(TOKEN_CUA.noi), mat: mauToken(TOKEN_CUA.mat), viec: mauToken(TOKEN_CUA.viec), khan: mauToken(TOKEN_CUA.khan), 'chua-gan': mauToken(TOKEN_CUA['chua-gan']) });
  }, []);
  const vien = useMemo(() => mauToken('--nen-1'), []);
  const nhan = useMemo(() => mauToken('--nhan'), []);
  const nhanSang = useMemo(() => mauToken('--nhan-sang'), []);
  const coNen = vectorOk || nen.tt === 'ok';   // TK-34 thử: nền vector đang vẽ thì không vẽ lưới xám
  /*
   * ⚠ DỰNG LỚP NỀN NGAY TỪ LÚC CÒN ĐANG THỬ, không chờ phép thử xong.
   *   Phép thử là để BIẾT và để NÓI RA, không phải để cho phép. Chờ nó xong mới dựng lớp là
   *   bắt mọi người đợi thêm một vòng mạng, và nếu phép thử rơi thì ô bản đồ không bao giờ
   *   được thử lần nào — đúng chỗ hỏng mà lee gặp.
   */
  const veNen = nen.mau !== null && nen.tt !== 'hong' && nen.tt !== 'tat';

  return (
    <div className={tranKhung ? 'relative h-full' : 'space-y-2'} data-ban-do>
      {/* TK-45 §1: giữ lâu trên bản đồ không mở menu trình duyệt */}
      <div onContextMenu={(e) => e.preventDefault()} data-o="khung-ban-do"
        className={`relative isolate overflow-hidden ${tranKhung ? '' : 'rounded-nut border border-vien'} ${caoLop}
          ${nenToi && !vectorOk ? '[&_.leaflet-tile-pane]:[filter:invert(1)_hue-rotate(180deg)_brightness(.85)_saturate(.5)]' : ''}
          [&_.leaflet-popup-content-wrapper]:rounded-nut [&_.leaflet-popup-content-wrapper]:bg-nen-1 [&_.leaflet-popup-content-wrapper]:text-chu-1
          [&_.leaflet-popup-tip]:bg-nen-1 [&_.leaflet-container]:font-[inherit] [&_.popup-tu-phai_.leaflet-popup-tip-container]:hidden
          [&_.leaflet-bar]:!border-vien [&_.leaflet-bar_a]:!border-vien [&_.leaflet-bar_a]:!bg-nen-1 [&_.leaflet-bar_a]:!text-chu-1 [&_.leaflet-bar_a:hover]:!bg-nen-2
          [&_.leaflet-control-scale-line]:!border-chu-3 [&_.leaflet-control-scale-line]:!bg-nen-0/85 [&_.leaflet-control-scale-line]:!text-chu-2`}
      >
        <MapContainer
          center={[TOA_DO_MAC_DINH.lat, TOA_DO_MAC_DINH.lng]}
          zoom={11}
          zoomControl={false}
          attributionControl={false}
          className="h-full w-full"
          style={{ backgroundColor: 'rgb(var(--nen-2))', backgroundImage: coNen ? undefined : LUOI }}
        >
          <AttributionControl prefix={false} />
          {/* TK-33b (mẫu chạy được): +/− góc DƯỚI PHẢI, thước tỉ lệ góc dưới trái; góc trên trái cho thanh công cụ. */}
          <ZoomControl position="bottomright" />
          <ScaleControl position="bottomleft" imperial={false} />
          {vectorOk && nen.tt !== 'tat' && <Suspense fallback={null}><NenVector toi={nenToi} onHong={vectorHong} /></Suspense>}
          {!vectorOk && veNen && nen.mau && (
            <TileLayer
              url={nen.mau}
              attribution={nen.mau === NEN_MAC_DINH ? '© OpenStreetMap' : ''}
              eventHandlers={{ tileerror: () => nen.baoHong() }}
            />
          )}
          <KhopKhung diem={diemKhung && diemKhung.length > 0 ? diemKhung : diem} lan={lanKhop} nhuong={!!tuChon && cham.some((c) => c.id === tuChon)} />
          {mau && hienTu && <LopCham cham={cham} mau={mau} nhan={nhan} nhanSang={nhanSang} vien={vien} popupTu={popupTu} onChonTu={onChonTu} tuChon={tuChon} lanChon={lanChon} hienSuCo={hienSuCo} moPopupKhiChon={moPopupKhiChon} />}
          {lopThem}
          {mapRef && <LayMap mapRef={mapRef} />}
        </MapContainer>

        {/* Góc trên trái: thanh công cụ (TK-33b) rồi hộp lớp đèn (TK-31) — một cột, không đè nhau. */}
        {(thanhCongCu || hopTrai) && (
          <div className="pointer-events-none absolute left-2 right-2 top-2 z-[500] flex flex-col items-start gap-2 md:left-3.5 md:right-3.5 md:top-3.5">
            {thanhCongCu}
            {hopTrai && <div className="pointer-events-auto max-w-[min(360px,100%)] rounded-nut border border-vien bg-nen-0/95 px-2.5 py-2 text-xs text-chu-2" data-o="hop-lop-trai">{hopTrai}</div>}
          </div>
        )}
        {/* Chú giải góc DƯỚI TRÁI (trên thước tỉ lệ), mẫu: Hoạt động · Cảnh báo · Khẩn · Mất kết nối (XÁM) [+ Đèn]. */}
        <div className="pointer-events-none absolute bottom-9 left-3 z-[500] flex max-w-[calc(100%-4rem)] flex-col items-start gap-1.5">
          <button type="button" onClick={() => setMoChuGiai((v) => !v)} aria-expanded={moChuGiai} data-o="nut-chu-giai"
            className="pointer-events-auto rounded-nut border border-vien bg-nen-0/95 px-2.5 py-1 text-[11px] text-chu-2 hover:bg-nen-2">Chú giải</button>
          {moChuGiai && <ul className="pointer-events-auto m-0 flex list-none flex-wrap gap-x-3.5 gap-y-1 rounded-nut border border-vien bg-nen-0/95 px-[11px] py-2 text-[11px] text-chu-1" data-chu-giai>
            {CHU_GIAI.filter((g) => g.loai !== 'chua-gan' || cham.some((c) => c.loai === 'chua-gan')).map((g) => (
              <li key={g.loai} className="flex items-center gap-1">
                <svg width="7" height="7" viewBox="0 0 10 10" aria-hidden="true" className={g.lop}><circle cx="5" cy="5" r="5" fill="currentColor" /></svg>
                {g.ten}
              </li>
            ))}
            {(chuGiaiThem ?? []).map((g) => (
              <li key={g.ten} className="flex items-center gap-1">
                <svg width="7" height="7" viewBox="0 0 10 10" aria-hidden="true" className={g.lop}><circle cx="5" cy="5" r="5" fill="currentColor" /></svg>
                {g.ten}
              </li>
            ))}
          </ul>}
          {ghiChuDuoi && <p className="pointer-events-auto m-0 rounded-[3px] border border-vien-manh bg-nen-0/90 px-[9px] py-1.5 text-[10px] text-chu-2" data-o="ghi-chu-ban-do">{ghiChuDuoi}</p>}
        </div>

        {khongTuNao && (
          <div className="pointer-events-none absolute inset-0 z-[450] grid place-items-center">
            <p className="rounded-nut bg-nen-1 px-3 py-1.5 text-sm text-chu-2">Chưa có tủ nào gửi toạ độ</p>
          </div>
        )}
      </div>
      <div className={tranKhung ? 'pointer-events-auto absolute bottom-1 right-14 z-[500] flex flex-wrap gap-x-4 text-[10px] text-chu-3' : 'flex flex-wrap gap-x-4 gap-y-1 text-xs text-chu-3'}>
        {!vectorOk && nen.tt === 'dang-thu' && <span>Đang tải nền bản đồ…</span>}
        {!vectorOk && nen.tt === 'hong' && (
          <span data-nen-hong className="flex items-center gap-2">
            Không tải được nền bản đồ
            <button type="button" onClick={nen.thuLai} className="rounded-nut border border-vien px-2 py-0.5 text-chu-2 hover:text-chu-1">
              Thử lại
            </button>
          </span>
        )}
        {nen.tt === 'tat' && <span>Nền bản đồ đang tắt</span>}
        {khongToaDo > 0 && !khongTuNao && <span data-khong-toa-do>{khongToaDo} tủ chưa có toạ độ</span>}
      </div>
    </div>
  );
}

/** TK-31d: đưa `L.Map` ra ngoài cho thanh sửa vị trí đèn. */
function LayMap({ mapRef }: { mapRef: MutableRefObject<L.Map | null> }) {
  const map = useMap();
  useEffect(() => { mapRef.current = map; return () => { mapRef.current = null; }; }, [map, mapRef]);
  return null;
}
